import { Globe, Plus, Settings } from "lucide-react";
import { useNavigate } from "react-router";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@headerhawk/ui";
import { useSiteScope } from "@/lib/hooks/use-site-scope";
import { useCurrentRole } from "@/lib/hooks/use-current-role";
import { ADD_SITE_HREF, siteSetupHref } from "@/lib/sites/links";

const ALL_SITES = "all";
// Not site ids: a site id is a UUID, so neither can collide with one.
const ADD_SITE = "action:add-site";
const SET_UP_SITE = "action:set-up-site";

/**
 * The header's site scope (ADR 013, principle 2): all sites, or one. Every
 * section reads it, and it stays in the URL, so a link carries the scope it
 * was chosen with.
 *
 * Its menu ends with the way to add a site and, with one in scope, to set
 * it up (task 454): for someone new those are the first things to do, and
 * they were four clicks deep in Settings. A tenant with one site has no
 * choice of scope, so its menu is that site and the two actions.
 *
 * A tenant with no sites (task 495: one that deleted them all keeps the
 * dashboard) still has the control, offering only Add a site. It is the one
 * item, so for a role that cannot add a site it is drawn disabled with the
 * reason under it, rather than leaving an empty menu.
 */
export function SiteScopeSelect() {
  const { siteId, sites, setSiteId, isLoading } = useSiteScope();
  const { can, whyNot } = useCurrentRole();
  const navigate = useNavigate();

  if (isLoading) return null;

  const none = sites.length === 0;
  const single = sites.length === 1;
  const canAddSite = can("POST /sites");
  const cannotAddReason = whyNot("POST /sites", "add a site");

  return (
    <Select
      // No value with no sites, so the trigger shows its placeholder.
      value={none ? "" : (siteId ?? ALL_SITES)}
      onValueChange={(value) => {
        // The two actions navigate; the value stays the scope it was.
        if (value === ADD_SITE) navigate(ADD_SITE_HREF);
        else if (value === SET_UP_SITE && siteId)
          navigate(siteSetupHref(siteId));
        else if (!single && !none)
          setSiteId(value === ALL_SITES ? undefined : value);
      }}
    >
      <SelectTrigger
        className="h-9 w-[150px] sm:w-[200px]"
        aria-label="Site"
        data-testid="site-scope"
      >
        {/* A div, not a span: the trigger line-clamps its direct span
            children, which turns a flex row into a vertical box and stacks
            the icon above the name. */}
        <div className="flex min-w-0 items-center gap-2">
          <Globe className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
          {/* Radix's SelectValue drops className, so the truncation is ours. */}
          <span className="min-w-0 truncate">
            <SelectValue placeholder="Add a site" />
          </span>
        </div>
      </SelectTrigger>
      <SelectContent>
        {!single && !none && (
          <SelectItem value={ALL_SITES}>All sites</SelectItem>
        )}
        {sites.map((site) => (
          <SelectItem key={site.id} value={site.id}>
            {site.name}
          </SelectItem>
        ))}
        {!none && (siteId || canAddSite) && <SelectSeparator />}
        {/* With sites, left out rather than disabled for a role that cannot
            add one: Settings › Sites gives the reason. */}
        {(canAddSite || none) && (
          <SelectItem
            value={ADD_SITE}
            disabled={!canAddSite}
            data-testid="site-scope-add"
          >
            <span className="flex items-center gap-2">
              <Plus className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
              Add a site
            </span>
          </SelectItem>
        )}
        {none && !canAddSite && cannotAddReason && (
          <p
            className="max-w-[240px] py-1.5 pl-8 pr-2 text-xs text-muted-foreground"
            data-testid="site-scope-add-reason"
          >
            {cannotAddReason}
          </p>
        )}
        {siteId && (
          <SelectItem value={SET_UP_SITE} data-testid="site-scope-set-up">
            <span className="flex items-center gap-2">
              <Settings className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
              Set up this site
            </span>
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}
